import { UsageChartWrapper, type BasicUsageChartProps } from "./UsageChartWrapper.js";
import { TIERED_PRODUCTS, TieredPriceProductId } from "@project/common";
import { useIntl } from "react-intl";
import { useMemoizedBarSeries } from "../../../hooks/chart.js";
import { IconMap } from "@tabler/icons-react";
import { EChartTimeseries } from "../../../components/EChartTimeseries.js";
import { FormattedMessage } from "react-intl";
import { useCustomStylesUsageSum } from "./hooks/fetchSums.js";
import { checkIsApproachingLimit } from "./utils/limits.js";
import { useCustomStylesUsage } from "./hooks/fetchUsages.js";
import { resolveUsageEventDeltas } from "./utils/usageEventsProcessors.js";
import { Text } from "@mantine/core";
import { useProductNames } from "./hooks/productLabels.js";

export const CustomStylesUsageChart = ({
  from,
  to,
  interval,
  addToDetailedView,
}: BasicUsageChartProps) => {
  const intl = useIntl();
  const getName = useProductNames();

  const {
    customStylesUsage,
    isLoadingStyles: isLoadingStylesHist,
    isErrorStyles: isErrorStylesHist,
  } = useCustomStylesUsage({ from, to, interval });

  const {
    usageSum,
    isLoadingStyles: isLoadingStylesSum,
    isErrorStyles: isErrorStylesSum,
  } = useCustomStylesUsageSum({ to });

  const title = getName(TieredPriceProductId.TieredCustomStyle);

  const series = useMemoizedBarSeries(
    resolveUsageEventDeltas(customStylesUsage, usageSum),
    title,
    TIERED_PRODUCTS[TieredPriceProductId.TieredCustomStyle].freeTierAllowance,
    intl.formatMessage({ defaultMessage: "1 map free", id: "allowance.map" }),
  );

  return (
    <UsageChartWrapper
      title={title}
      iconColor="success.2"
      icon={<IconMap size={20} />}
      isLoading={isLoadingStylesHist || isLoadingStylesSum}
      hasError={isErrorStylesHist || isErrorStylesSum}
      addToDetailedView={addToDetailedView}
      hasUsage={customStylesUsage.length > 0}
      isApproachingLimit={checkIsApproachingLimit(
        usageSum,
        TieredPriceProductId.TieredCustomStyle,
      )}
    >
      <EChartTimeseries series={series} />
      <Text mt="sm">
        <FormattedMessage
          defaultMessage="Active custom maps: {usageSum}"
          values={{ usageSum }}
          id="customStyles.sum"
        />
      </Text>
    </UsageChartWrapper>
  );
};
